<template>
	<transition name="back-to-top" mode="out-in">
		<div v-show="isVisible" class="back-to-top-button" :class="{ 'is-visible': isVisible }" :style="{ 
        '--button-size': size + 'px',
        '--button-bg': backgroundColor,
        '--button-hover-bg': hoverBackgroundColor,
        '--button-color': textColor,
        '--position-right': positionRight + 'px',
        '--position-bottom': positionBottom + 'px'
      }" @click="scrollToTop" :title="tooltip">
			<div class="back-to-top-icon">
				<slot name="icon">
					<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
						<path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" />
					</svg>
				</slot>
			</div>
		</div>
	</transition>
</template>

<script>
	export default {
		name: 'BackToTop',
		props: {
			// 触发显示的滚动距离
			visibilityHeight: {
				type: Number,
				default: 300,
				validator: value => value >= 0
			},
			// 按钮大小
			size: {
				type: Number,
				default: 50,
				validator: value => value > 0
			},
			// 按钮背景色
			backgroundColor: {
				type: String,
				default: '#409eff'
			},
			// 悬停背景色
			hoverBackgroundColor: {
				type: String,
				default: '#337ecc'
			},
			// 文字/图标颜色
			textColor: {
				type: String,
				default: '#ffffff'
			},
			// 距离右边的距离
			positionRight: {
				type: Number,
				default: 20
			},
			// 距离底部的距离
			positionBottom: {
				type: Number,
				default: 20
			},
			// 滚动行为
			behavior: {
				type: String,
				default: 'smooth',
				validator: value => ['smooth', 'auto'].includes(value)
			},
			// 提示文字
			tooltip: {
				type: String,
				default: '回到顶部'
			},
			// 目标滚动位置
			target: {
				type: Number,
				default: 0,
				validator: value => value >= 0
			},
			// 是否使用Intersection Observer
			useIntersectionObserver: {
				type: Boolean,
				default: true
			}
		},
		data() {
			return {
				isVisible: false,
				observer: null,
				targetElement: null
			}
		},
		mounted() {
			// console.log('BackToTop组件已创建')
			this.initScrollListener()
		},
		beforeDestroy() {
			// console.log('BackToTop组件清理资源')
			this.cleanupListener()
		},
		methods: {
			initScrollListener() {
				if (this.useIntersectionObserver && 'IntersectionObserver' in window) {
					this.initIntersectionObserver()
				} else {
					this.initScrollEventListener()
				}
			},

			initIntersectionObserver() {
				// 创建一个虚拟元素作为观察目标
				this.targetElement = document.createElement('div')
				this.targetElement.style.position = 'absolute'
				this.targetElement.style.top = this.visibilityHeight + 'px'
				this.targetElement.style.height = '1px'
				this.targetElement.style.width = '1px'
				this.targetElement.style.opacity = '0'
				this.targetElement.style.pointerEvents = 'none'
				document.body.appendChild(this.targetElement)

				this.observer = new IntersectionObserver(
					(entries) => {
						entries.forEach(entry => {
							// 当目标元素不在视口中时，说明已经滚动超过了visibilityHeight
							this.isVisible = !entry.isIntersecting
						})
					}, {
						root: null,
						rootMargin: '0px',
						threshold: 0
					}
				)

				this.observer.observe(this.targetElement)
			},

			initScrollEventListener() {
				// 降级方案：使用scroll事件
				let ticking = false

				this.scrollHandler = () => {
					if (!ticking) {
						requestAnimationFrame(() => {
							this.updateVisibility()
							ticking = false
						})
						ticking = true
					}
				}

				window.addEventListener('scroll', this.scrollHandler, {
					passive: true
				})
				// 初始检查
				this.updateVisibility()
			},

			updateVisibility() {
				const scrollTop = window.pageYOffset || document.documentElement.scrollTop
				this.isVisible = scrollTop > this.visibilityHeight
			},

			scrollToTop() {
				// 触发点击事件
				this.$emit('click')

				try {
					window.scrollTo({
						top: this.target,
						behavior: this.behavior
					})

					// 触发滚动完成事件
					this.$emit('scroll-to-top')
				} catch (error) {
					// 降级处理，兼容老版本浏览器
					console.warn('平滑滚动不支持，使用降级方案')
					window.scrollTo(0, this.target)
					this.$emit('scroll-to-top')
				}
			},

			cleanupListener() {
				if (this.observer) {
					this.observer.disconnect()
					this.observer = null
				}

				if (this.targetElement) {
					document.body.removeChild(this.targetElement)
					this.targetElement = null
				}

				if (this.scrollHandler) {
					window.removeEventListener('scroll', this.scrollHandler)
					this.scrollHandler = null
				}
			}
		}
	}
</script>

<style lang="scss" scoped>
	.back-to-top-button {
		position: fixed;
		right: var(--position-right);
		bottom: var(--position-bottom);
		width: var(--button-size);
		height: var(--button-size);
		background-color: var(--button-bg);
		color: var(--button-color);
		border-radius: 50%;
		cursor: pointer;
		z-index: 1000;
		display: flex;
		align-items: center;
		justify-content: center;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
		transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
		user-select: none;

		&:hover {
			background-color: var(--button-hover-bg);
			transform: translateY(-2px);
			box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
		}

		&:active {
			transform: translateY(0);
			box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
		}

		.back-to-top-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 100%;
			height: 100%;

			svg {
				transition: transform 0.2s ease;
			}
		}

		&:hover .back-to-top-icon svg {
			transform: translateY(-1px);
		}
	}

	// 过渡动画
	.back-to-top-enter-active {
		transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
	}

	.back-to-top-leave-active {
		transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
	}

	.back-to-top-enter {
		opacity: 0;
		transform: translateY(20px) scale(0.8);
	}

	.back-to-top-leave-to {
		opacity: 0;
		transform: translateY(20px) scale(0.8);
	}

	// 响应式设计
	@media (max-width: 768px) {
		.back-to-top-button {
			right: 15px;
			bottom: 15px;

			// 移动端可以考虑隐藏或调整样式
			&.mobile-hidden {
				display: none;
			}
		}
	}

	@media (max-width: 480px) {
		.back-to-top-button {
			width: 45px;
			height: 45px;
			right: 10px;
			bottom: 10px;
		}
	}
</style>